import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import StockAnalyzerSidebar from './StockAnalyzerSidebar';
import { NAV_SECTIONS, TAB_KEYS, DEFAULT_TAB } from './navSections';
import TickerInputSection from './TickerInputSection';
import FundamentalsTable from './FundamentalsTable';
import EPSTrendsChart from './EPSTrendsChart';
import ResearchFinancialsTable from '../Research/ResearchFinancialsTable';
import PriceChartModal from '../Research/PriceChartModal';
import FairValueTable from './FairValueTable';
import ComparablesTable from './ComparablesTable';
import ElasticityForm from './ElasticityForm';
import ElasticityTable from './ElasticityTable';
import HedgingForm from './HedgingForm';
import HedgingResults from './HedgingResults';
import ExplorerTab from './ExplorerTab';
import ScalpingTab from './Scalping/ScalpingTab';
import SpreadsTab from './Spreads/SpreadsTab';
import StraddlesTab from './Straddles/StraddlesTab';
import EarningsAnalysisTable from './EarningsAnalysisTable';
import AnalysisHistory from './AnalysisHistory';
import MacroDashboard from './MacroDashboard/MacroDashboard';
import CompanyScorePanel from './CompanyScore/CompanyScorePanel';
import ExportControls from './ExportControls';
import apiClient from '../../services/api';

function StockAnalyzerDashboard() {
  const { isAdmin } = useUser();
  const [analysisData, setAnalysisData] = useState({
    fundamentals: [],
    epsTrends: [],
    ratios: null,
    fairValue: [],
    comparables: [],
    elasticity: [],
    hedging: null,
    earnings: [],
    companyScore: null,
    sessionId: null,
    sessionName: '',
    processing: false,
    errors: []
  });
  const [elasticityProcessing, setElasticityProcessing] = useState(false);
  const [hedgingProcessing, setHedgingProcessing] = useState(false);
  const [earningsProcessing, setEarningsProcessing] = useState(false);
  const [companyScoreProcessing, setCompanyScoreProcessing] = useState(false);
  const [lastAnalyzedTickers, setLastAnalyzedTickers] = useState([]);

  // The active tool lives in the URL (?tab=spreads) so it survives a reload and
  // can be linked to directly. Deriving it rather than mirroring it into state
  // keeps a single source of truth; keeping the name `setActiveTab` means every
  // existing caller (the post-analysis auto-switch, Explorer's
  // onNavigateToInput, the nav itself) works unchanged.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  // Validated so a stale or hand-edited ?tab= can't land on the switch's
  // `default: return null` and render a blank panel.
  const activeTab = TAB_KEYS.has(requestedTab) ? requestedTab : DEFAULT_TAB;
  const setActiveTab = useCallback((key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    });
  }, [setSearchParams]);

  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      return localStorage.getItem('stockAnalyzer_navCollapsed') === 'true';
    } catch {
      return false;
    }
  });
  const toggleNavCollapsed = useCallback(() => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('stockAnalyzer_navCollapsed', String(next));
      } catch {
        /* private mode — collapse still works, just isn't remembered */
      }
      return next;
    });
  }, []);

  const [watchedStocks, setWatchedStocks] = useState([]);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [ratiosYears, setRatiosYears] = useState(5); // 5 or 10 years
  const [ratiosProcessing, setRatiosProcessing] = useState(false);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartTicker, setChartTicker] = useState(null);

  useEffect(() => {
    // Load watched stocks on component mount
    loadWatchedStocks();
    loadAnalysisHistory();
  }, []);

  const loadWatchedStocks = async () => {
    try {
      const response = await apiClient.get('/stock-analyzer/watched-stocks/');
      setWatchedStocks(response.data.watched_stocks || []);
    } catch (error) {
      console.error('Error loading watched stocks:', error);
    }
  };

  const loadAnalysisHistory = async () => {
    try {
      const response = await apiClient.get('/stock-analyzer/history/');
      setAnalysisHistory(response.data.sessions || []);
    } catch (error) {
      console.error('Error loading analysis history:', error);
    }
  };

  const handleAnalysisSubmit = async (formData) => {
    setAnalysisData(prev => ({ ...prev, processing: true, errors: [] }));

    // Debug logging
    console.log('Submitting analysis request:', formData);

    try {
      const response = await apiClient.post('/stock-analyzer/analyze/', formData);
      console.log('Analysis response:', response.data);

      // Merge rather than replace: a bare object literal here would drop every
      // key it doesn't list (elasticity, hedging, fairValue, comparables,
      // earnings, companyScore) to undefined, and the tools that read those
      // arrays directly — e.g. `analysisData.elasticity.length` in the
      // elasticity tab — would throw the next time you opened them.
      setAnalysisData(prev => ({
        ...prev,
        fundamentals: response.data.fundamentals_results || [],
        epsTrends: response.data.eps_trends_results || [],
        sessionId: response.data.session_id,
        sessionName: response.data.session_name,
        processing: false,
        errors: response.data.errors || []
      }));

      // Store tickers for Explorer tab
      if (formData.tickers) {
        setLastAnalyzedTickers(formData.tickers);
      }

      // Switch to appropriate tab based on analysis types
      if (formData.analysis_types.includes('fundamentals')) {
        setActiveTab('fundamentals');
      } else if (formData.analysis_types.includes('eps_trends')) {
        setActiveTab('eps-trends');
      } else if (formData.analysis_types.includes('financials')) {
        setActiveTab('financials');
      } else if (formData.analysis_types.includes('fair_value')) {
        setActiveTab('fair-value');
      }

      // If financials was selected, fetch financials data separately
      if (formData.analysis_types.includes('financials')) {
        fetchRatiosData(formData.tickers, formData.years || ratiosYears);
      }

      // If fair_value was selected, fetch fair value data separately
      if (formData.analysis_types.includes('fair_value')) {
        fetchFairValueData(formData.tickers);
      }

      // If comparables was selected, fetch comparables data separately
      if (formData.analysis_types.includes('comparables')) {
        fetchComparablesData(formData.tickers);
      }

      // If earnings was selected, fetch earnings data separately
      if (formData.analysis_types.includes('earnings')) {
        fetchEarningsData(formData.tickers);
      }

      // If company_score was selected, fetch it separately
      if (formData.analysis_types.includes('company_score')) {
        fetchCompanyScoreData(formData.tickers);
      }

      // Refresh history
      loadAnalysisHistory();

    } catch (error) {
      console.error('Analysis failed:', error);
      console.error('Error response:', error.response?.data);

      // Extract detailed error information
      const errorMessage = error.response?.data?.error || 'Analysis failed';
      const errorDetails = error.response?.data?.details || {};

      let detailedErrors = [errorMessage];

      // Add specific validation errors if available
      if (typeof errorDetails === 'object' && errorDetails !== null) {
        Object.entries(errorDetails).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            messages.forEach(msg => detailedErrors.push(`${field}: ${msg}`));
          } else {
            detailedErrors.push(`${field}: ${messages}`);
          }
        });
      }

      setAnalysisData(prev => ({
        ...prev,
        processing: false,
        errors: detailedErrors
      }));
    }
  };

  const handleAddToWatchlist = async (ticker, notes = '') => {
    try {
      await apiClient.post('/stock-analyzer/watched-stocks/', {
        ticker: ticker.toUpperCase(),
        notes
      });
      loadWatchedStocks();
    } catch (error) {
      console.error('Error adding to watchlist:', error);
    }
  };

  const fetchRatiosData = async (tickers, years) => {
    setRatiosProcessing(true);
    try {
      const response = await apiClient.post('/stock-analyzer/ratios/', {
        tickers,
        years
      });
      console.log('Ratios response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        ratios: response.data
      }));
    } catch (error) {
      console.error('Ratios fetch failed:', error);
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to fetch historical ratios']
      }));
    } finally {
      setRatiosProcessing(false);
    }
  };

  const handleRatiosYearsChange = (years) => {
    setRatiosYears(years);
    // Re-fetch ratios data if we have tickers
    if (analysisData.ratios?.tickers?.length > 0) {
      fetchRatiosData(analysisData.ratios.tickers, years);
    }
  };

  const handleTickerClick = (ticker) => {
    setChartTicker(ticker);
    setChartModalOpen(true);
  };

  const fetchFairValueData = async (tickers) => {
    try {
      const response = await apiClient.post('/stock-analyzer/fair-value/', { tickers });
      console.log('Fair value response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        fairValue: response.data.fair_value_results || []
      }));
    } catch (error) {
      console.error('Fair value fetch failed:', error);
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to fetch fair value data']
      }));
    }
  };

  const fetchComparablesData = async (tickers) => {
    try {
      const response = await apiClient.post('/stock-analyzer/comparables/', { tickers });
      console.log('Comparables response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        comparables: response.data.comparables_results || []
      }));
    } catch (error) {
      console.error('Comparables fetch failed:', error);
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to fetch comparables data']
      }));
    }
  };

  const fetchEarningsData = async (tickers) => {
    setEarningsProcessing(true);
    try {
      const response = await apiClient.post('/stock-analyzer/earnings/', { tickers });
      console.log('Earnings response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        earnings: response.data.earnings_results || []
      }));
    } catch (error) {
      console.error('Earnings fetch failed:', error);
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to fetch earnings data']
      }));
    } finally {
      setEarningsProcessing(false);
    }
  };

  const fetchCompanyScoreData = async (tickers) => {
    setCompanyScoreProcessing(true);
    try {
      const response = await apiClient.post('/stock-analyzer/company-score/', { tickers });
      console.log('Company score response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        companyScore: response.data
      }));
    } catch (error) {
      console.error('Company score fetch failed:', error);
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to fetch company scores']
      }));
    } finally {
      setCompanyScoreProcessing(false);
    }
  };

  const fetchElasticityData = async (requestData) => {
    setElasticityProcessing(true);
    setAnalysisData(prev => ({ ...prev, errors: [] }));
    try {
      const response = await apiClient.post('/stock-analyzer/elasticity/', requestData);
      console.log('Elasticity response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        elasticity: response.data.elasticity_results || []
      }));
    } catch (error) {
      console.error('Elasticity fetch failed:', error);
      const errorMessage = error.response?.data?.error || 'Failed to fetch elasticity data';
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, errorMessage]
      }));
    } finally {
      setElasticityProcessing(false);
    }
  };


  const fetchHedgingData = async (requestData) => {
    setHedgingProcessing(true);
    setAnalysisData(prev => ({ ...prev, errors: [] }));
    try {
      const response = await apiClient.post('/stock-analyzer/hedging/', requestData);
      console.log('Hedging response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        hedging: response.data.hedging_results || null
      }));
    } catch (error) {
      console.error('Hedging fetch failed:', error);
      const errorMessage = error.response?.data?.error || 'Failed to build hedge';
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, errorMessage]
      }));
    } finally {
      setHedgingProcessing(false);
    }
  };


  const handleExport = async (exportOptions) => {
    try {
      const response = await apiClient.post('/stock-analyzer/export/', exportOptions, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'stock_analysis.csv';

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'input':
        return (
          <TickerInputSection
            onAnalysisSubmit={handleAnalysisSubmit}
            watchedStocks={watchedStocks}
            onAddToWatchlist={handleAddToWatchlist}
            processing={analysisData.processing}
            errors={analysisData.errors}
          />
        );

      case 'fundamentals':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Fundamentals Analysis</h2>
              <ExportControls
                sessionId={analysisData.sessionId}
                analysisTypes={['fundamentals']}
                onExport={handleExport}
              />
            </div>
            <FundamentalsTable
              data={analysisData.fundamentals}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'eps-trends':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">EPS Trends Analysis</h2>
              <ExportControls
                sessionId={analysisData.sessionId}
                analysisTypes={['eps_trends']}
                onExport={handleExport}
              />
            </div>
            <EPSTrendsChart
              data={analysisData.epsTrends}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'financials':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Historical Financials</h2>
              {ratiosProcessing && (
                <div className="flex items-center text-gray-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Loading financials...
                </div>
              )}
            </div>
            <ResearchFinancialsTable
              data={analysisData.ratios}
              years={ratiosYears}
              onYearsChange={handleRatiosYearsChange}
              onTickerClick={handleTickerClick}
            />
          </div>
        );

      case 'fair-value':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Fair Value Analysis</h2>
            </div>
            <FairValueTable
              data={analysisData.fairValue}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'comparables':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Comparables Analysis</h2>
            </div>
            <ComparablesTable
              data={analysisData.comparables}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'elasticity':
        return (
          <div className="space-y-8">
            <ElasticityForm
              onSubmit={fetchElasticityData}
              processing={elasticityProcessing}
            />
            {analysisData.elasticity.length > 0 && (
              <div>
                <div className="border-t border-gray-700 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Elasticity Results</h3>
                  <ElasticityTable
                    data={analysisData.elasticity}
                    onAddToWatchlist={handleAddToWatchlist}
                    watchedStocks={watchedStocks}
                  />
                </div>
              </div>
            )}
            {analysisData.errors.length > 0 && (
              <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
                <h3 className="text-red-400 font-medium mb-2">Errors:</h3>
                <ul className="text-red-300 text-sm space-y-1">
                  {analysisData.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'hedge':
        return (
          <div className="space-y-8">
            <HedgingForm
              onSubmit={fetchHedgingData}
              processing={hedgingProcessing}
            />
            {analysisData.hedging && (
              <div className="border-t border-gray-700 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Hedge Analysis</h3>
                <HedgingResults data={analysisData.hedging} />
              </div>
            )}
            {analysisData.errors.length > 0 && (
              <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
                <h3 className="text-red-400 font-medium mb-2">Errors:</h3>
                <ul className="text-red-300 text-sm space-y-1">
                  {analysisData.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'explorer':
        return (
          <ExplorerTab
            tickers={lastAnalyzedTickers}
            onNavigateToInput={() => setActiveTab('input')}
          />
        );

      case 'scalping':
        return (
          <ScalpingTab
            onAddToWatchlist={handleAddToWatchlist}
            watchedStocks={watchedStocks}
          />
        );

      case 'spreads':
        return <SpreadsTab />;

      case 'straddles':
        return <StraddlesTab />;

      case 'earnings':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Earnings Analysis</h2>
              {earningsProcessing && (
                <div className="flex items-center text-gray-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Loading earnings data...
                </div>
              )}
            </div>
            <EarningsAnalysisTable
              data={analysisData.earnings}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'company-score':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Company Score</h2>
              {companyScoreProcessing && (
                <div className="flex items-center text-gray-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Scoring stocks...
                </div>
              )}
            </div>
            <CompanyScorePanel data={analysisData.companyScore} />
          </div>
        );

      case 'macro':
        return <MacroDashboard />;

      case 'history':
        return (
          <AnalysisHistory
            sessions={analysisHistory}
            onLoadSession={(sessionId) => {
              // Load session data - could implement this later
              console.log('Load session:', sessionId);
            }}
            onDeleteSession={(sessionId) => {
              // Delete session - could implement this later
              console.log('Delete session:', sessionId);
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      {/* Wider than the app's usual max-w-7xl to offset the sidebar: with it
          expanded the content column still gets more room than the old
          full-width tab layout gave it. */}
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold mb-2">Stock Analyzer</h1>
            <p className="text-gray-400">
              Comprehensive fundamental analysis and EPS trends tracking
            </p>
          </div>
          {isAdmin && (
            // Admin-only entry point. The Scoring Builder is hidden from
            // Plus/Premium users (TierFeatureAccess disabled in
            // 0022_disable_scoring_builder_for_users) while it's polished;
            // is_staff bypass keeps the route reachable for admins.
            <Link
              to="/stock-analyzer/scoring-builder"
              className="px-3 py-2 text-sm rounded-md border border-blue-500/60 text-blue-300 hover:bg-blue-600/20 transition whitespace-nowrap"
              title="Admin only — feature is hidden from end users while in development"
            >
              Open Scoring Builder →
            </Link>
          )}
        </div>

        {/* Navigation + content. `min-w-0` on the content column is load-bearing:
            without it the flex child refuses to shrink below its intrinsic
            width and this module's wide tables (Financials, Explorer, the
            spread heatmap) stretch the whole page instead of scrolling
            inside their own containers. */}
        <div className="flex flex-col lg:flex-row gap-6">
          <StockAnalyzerSidebar
            sections={NAV_SECTIONS}
            activeTab={activeTab}
            onSelect={setActiveTab}
            collapsed={navCollapsed}
            onToggleCollapsed={toggleNavCollapsed}
          />

          <main className="flex-1 min-w-0 bg-gray-800 rounded-lg p-6">
            {renderTabContent()}
          </main>
        </div>

        {/* Status Bar */}
        {analysisData.processing && (
          <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing analysis...
            </div>
          </div>
        )}

        {/* Error Display */}
        {analysisData.errors.length > 0 && (
          <div className="fixed bottom-4 left-4 max-w-md">
            {analysisData.errors.map((error, index) => (
              <div key={index} className="bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg mb-2">
                {error}
              </div>
            ))}
          </div>
        )}

        {/* Price Chart Modal */}
        <PriceChartModal
          isOpen={chartModalOpen}
          onClose={() => setChartModalOpen(false)}
          ticker={chartTicker}
        />
      </div>
    </div>
  );
}

export default StockAnalyzerDashboard;